import { IdGenerator, IdGeneratorEntityType } from '../services/IdGenerator';
import { splitMultiWordClipTokens } from '../services/transcriptTokenUtils';
import { prepareTranscriptTokens } from '../services/transcriptValidations';

import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  TimelinePositionPropsNotNull,
  TranscriptTextToken,
} from '../types';
import type { TranscriptElementLegacy } from '@src/types.ts/transcript';

export class ClipTranscriptSegment {
  public readonly id: string = IdGenerator.shortRandomId();
  private words: ClipTranscriptTextToken[];
  private wordsWithPunctuation: ClipTranscriptToken[];
  private mediaStartTime: number; // media anchored start time.
  private mediaEndTime: number; // media anchored end time.

  static createFromElements(
    transcriptTokens: readonly TranscriptElementLegacy[],
    fromTime: number,
    toTime: number,
    mediaStartTime: number,
    mediaEndTime: number,
    // Keeps forced-aligned word times (no 100 ms minimum).
    forcedAlignment: boolean = false,
  ): ClipTranscriptSegment {
    // Filter elements based on the time range, including relevant punctuation
    const filteredElements = this.filterElements(
      transcriptTokens,
      fromTime,
      toTime,
    );

    // Fix zero-duration text elements
    const fixedElements = prepareTranscriptTokens(
      filteredElements,
      forcedAlignment,
    );

    // Convert to ClipTranscriptToken format with relative timestamps
    let clipTokens = this.getElementsWithIdsAndTimelineRelativeTimestamps(
      fixedElements,
      fromTime,
      mediaStartTime,
      mediaEndTime - mediaStartTime,
    );

    // Split multi-word text elements into individual word tokens
    clipTokens = splitMultiWordClipTokens(clipTokens);

    // Create the segment with the processed elements
    return new ClipTranscriptSegment(clipTokens, mediaStartTime, mediaEndTime);
  }

  /**
   * Constructs a TranscriptSegment from the original transcription, filtered by time range
   * @param originalTranscription The full array of transcript elements
   * @param mediaStartTime Media start time for this segment
   * @param mediaEndTime Media end time for this segment
   */
  constructor(
    wordsWithPunctuation: ClipTranscriptToken[],
    mediaStartTime: number,
    mediaEndTime: number,
  ) {
    this.mediaStartTime = mediaStartTime;
    this.mediaEndTime = mediaEndTime;

    // Filter elements based on time range
    this.words = wordsWithPunctuation.filter(
      (el): el is ClipTranscriptTextToken => el.type === 'text',
    );
    this.wordsWithPunctuation = [...wordsWithPunctuation];
  }

  get mediaStart(): number {
    return this.mediaStartTime;
  }

  get mediaEnd(): number {
    return this.mediaEndTime;
  }

  /**
   * Replaces element IDs with freshly generated ones to avoid collisions
   * with active VideoTranscriptElements that carry the same original IDs.
   */
  regenerateElementIds(): void {
    const generator = IdGenerator.get(
      IdGeneratorEntityType.TranscriptToken,
      'hex',
    );
    for (const token of this.wordsWithPunctuation) {
      // PLAT-10611 — capture the story-space id before it is overwritten so the
      // translation join can still match this token by UUID.
      token.source_uuid ??= token.id;
      token.id = generator.getNextIdHex();
    }
  }

  get firstWord(): ClipTranscriptTextToken | null {
    return this.words.length > 0 ? this.words[0] : null;
  }

  get lastWord(): ClipTranscriptTextToken | null {
    return this.words.length > 0 ? this.words[this.words.length - 1] : null;
  }

  get hasWordsRemoved(): boolean {
    return this.wordsWithPunctuation.some(
      el => el.state === 'removed' || el.state === 'cut',
    );
  }

  get hasWordsAdded(): boolean {
    return this.wordsWithPunctuation.some(el => el.state === 'added');
  }

  // get precedingWord(): TranscriptTextElement | null {
  //   return this._precedingWord;
  // }

  // get followingWord(): TranscriptTextElement | null {
  //   return this._followingWord;
  // }

  // todo: consider moving to TranscriptTrackManager or moving split to here.
  // to reduce places where related logic exist
  concat(segment: ClipTranscriptSegment): ClipTranscriptSegment {
    const newWordsWithPunctuation = [
      ...this.wordsWithPunctuation,
      ...segment.wordsWithPunctuation.map(el =>
        el.type === 'text'
          ? {
              ...el,
              relative_ts:
                el.relative_ts + this.mediaEndTime - this.mediaStartTime,
              relative_end_ts:
                el.relative_end_ts + this.mediaEndTime - this.mediaStartTime,
            }
          : { ...el },
      ),
    ];

    let mergedSegment = new ClipTranscriptSegment(
      newWordsWithPunctuation,
      this.mediaStartTime,
      segment.mediaEndTime,
    );

    // After creating the merged segment, restore timestamps for boundary words
    // Find the last word of the first segment (boundary word that may have been trimmed at end)
    const lastWordOfFirstSegment = this.words.at(-1);
    const firstSegmentLastWordIndex = lastWordOfFirstSegment
      ? mergedSegment.wordsWithPunctuation.indexOf(lastWordOfFirstSegment)
      : -1;

    // Find the first word of the second segment (boundary word that may have been trimmed at start)
    const secondSegmentFirstWordIndex =
      mergedSegment.wordsWithPunctuation.findIndex(
        (token, index) =>
          index >= this.wordsWithPunctuation.length && token.type === 'text',
      );

    // Restore the last word of first segment if it exists
    if (firstSegmentLastWordIndex !== -1) {
      mergedSegment = mergedSegment.restoreTimestampAtPosition(
        firstSegmentLastWordIndex,
      );
    }

    // Restore the first word of second segment if it exists
    if (secondSegmentFirstWordIndex !== -1) {
      mergedSegment = mergedSegment.restoreTimestampAtPosition(
        secondSegmentFirstWordIndex,
      );
    }

    return mergedSegment;
  }

  /**
   * Restores trimmed word timestamps for a specific word at the given index
   * This allows words to expand into the newly available space from merged segments
   * @param wordIndex Index in wordsWithPunctuation array of the word to restore
   * @returns New segment with restored timestamps for the specified word
   */
  private restoreTimestampAtPosition(wordIndex: number): ClipTranscriptSegment {
    const restoredWords = [...this.wordsWithPunctuation];
    const totalSegmentDuration = this.mediaEndTime - this.mediaStartTime;

    // Validate index and ensure it's a text token
    if (wordIndex < 0 || wordIndex >= restoredWords.length) {
      console.warn(`Invalid word index ${wordIndex} for restoration`);
      return this;
    }

    const token = restoredWords[wordIndex];
    if (token.type !== 'text') {
      console.warn(
        `Token at index ${wordIndex} is not a text token, skipping restoration`,
      );
      return this;
    }

    const word = token as ClipTranscriptTextToken;
    const originalWordDuration = word.media_end_ts - word.media_ts;
    const currentWordDuration = word.relative_end_ts - word.relative_ts;

    // Skip if word is not trimmed
    if (Math.abs(originalWordDuration - currentWordDuration) < 0.001) {
      return this;
    }

    // Calculate ideal restored timestamps based on media times
    const idealRelativeStart = word.media_ts - this.mediaStartTime;
    const idealRelativeEnd = word.media_end_ts - this.mediaStartTime;

    // Find constraints from neighboring words
    const prevWord = ClipTranscriptSegment.findPreviousTextWord(
      wordIndex,
      restoredWords,
    );
    const nextWord = ClipTranscriptSegment.findNextTextWord(
      wordIndex,
      restoredWords,
    );

    // Calculate available space for restoration
    const earliestStart = prevWord ? prevWord.relative_end_ts : 0;
    const latestEnd = nextWord ? nextWord.relative_ts : totalSegmentDuration;

    // Calculate the best possible restoration within constraints
    let newRelativeStart = Math.max(idealRelativeStart, earliestStart);
    let newRelativeEnd = Math.min(idealRelativeEnd, latestEnd);

    // Ensure we don't make the word shorter than it currently is
    const minDuration = currentWordDuration;
    if (newRelativeEnd - newRelativeStart < minDuration) {
      // Extend as much as possible while respecting constraints
      if (newRelativeStart + minDuration <= latestEnd) {
        newRelativeEnd = newRelativeStart + minDuration;
      } else if (newRelativeEnd - minDuration >= earliestStart) {
        newRelativeStart = newRelativeEnd - minDuration;
      }
    }

    // Apply restoration if there's an improvement
    const newDuration = newRelativeEnd - newRelativeStart;
    if (newDuration > currentWordDuration + 0.001) {
      const restoredWord: ClipTranscriptTextToken = {
        ...word,
        relative_ts: newRelativeStart,
        relative_end_ts: newRelativeEnd,
      };
      restoredWords[wordIndex] = restoredWord;
    } else if (newDuration < currentWordDuration - 0.001) {
      // Log warning if we couldn't restore due to constraints
      console.warn(
        `Cannot fully restore word "${
          word.value
        }" timestamps: available space (${newDuration.toFixed(
          3,
        )}s) is less than current duration (${currentWordDuration.toFixed(
          3,
        )}s). Constraints: prev word ends at ${earliestStart.toFixed(
          3,
        )}, next word starts at ${latestEnd.toFixed(3)}`,
      );
    }

    return new ClipTranscriptSegment(
      restoredWords,
      this.mediaStartTime,
      this.mediaEndTime,
    );
  }

  /**
   * Find the previous text word in the words array
   */
  public static findPreviousTextWord(
    currentIndex: number,
    words: ClipTranscriptToken[],
  ): ClipTranscriptTextToken | null {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (words[i].type === 'text') {
        return words[i] as ClipTranscriptTextToken;
      }
    }
    return null;
  }

  /**
   * Find the next text word in the words array
   */
  public static findNextTextWord(
    currentIndex: number,
    words: ClipTranscriptToken[],
  ): ClipTranscriptTextToken | null {
    for (let i = currentIndex + 1; i < words.length; i++) {
      if (words[i].type === 'text') {
        return words[i] as ClipTranscriptTextToken;
      }
    }
    return null;
  }

  markElementsAsRestored(): void {
    this.wordsWithPunctuation.forEach(el => {
      if (el.state === 'removed' || el.state === 'cut') {
        delete el.state;
      }
    });
  }

  markElementsAsRemoved(): void {
    this.wordsWithPunctuation.forEach(el => {
      el.state = 'removed';
    });
  }

  markElementsAsAdded(): void {
    this.wordsWithPunctuation.forEach(el => {
      el.state = 'added';
    });
  }

  unmarkElements(): void {
    this.wordsWithPunctuation.forEach(el => {
      if (
        el.state === 'added' ||
        el.state === 'removed' ||
        el.state === 'cut'
      ) {
        delete el.state;
      }
    });
  }

  // /**
  //  * Checks if the segment contains the supplied transcript element
  //  * base on its start and end times
  //  * @param element The transcript element to check
  //  * @returns True if the element is contained within the segment
  //  */
  // contains(element: TranscriptTextElement): boolean {
  //   // Check if this element's time range is covered by the segment's time range
  //   const timeRangeContained =
  //     element.ts >= this.mediaStartTime && element.end_ts <= this.mediaEndTime;

  //   // Check if it's already in our words array (direct object reference)
  //   const wordAlreadyIncluded = this.words.includes(element);

  //   // Also check by word_index if not directly included
  //   const wordIndexMatch =
  //     !wordAlreadyIncluded &&
  //     this.words.some(
  //       word =>
  //         word.type === 'text' &&
  //         (word as TranscriptTextElement).word_index === element.word_index,
  //     );

  //   const result =
  //     timeRangeContained && (wordAlreadyIncluded || wordIndexMatch);

  //   return result;
  // }

  /**
   * Finds the closest text element before the given time
   * @param elements Array of transcript elements
   * @param time Time to search before
   * @returns The closest text element before the time or null if none found
   */
  public findPrecedingTextElement(
    time: number,
  ): ClipTranscriptTextToken | undefined {
    // Find the last text element that starts before our segment starts and word is at least half included
    return this.words
      .filter(
        el =>
          el.relative_ts < time &&
          time - el.relative_ts > el.relative_end_ts - time,
      )
      ?.at(-1);
  }

  /**
   * Finds the closest text element after the given time
   * @param elements Array of transcript elements
   * @param time Time to search after
   * @returns The closest text element after the time or null if none found
   */
  public findFollowingTextElement(
    relativeTime: number,
  ): ClipTranscriptToken | undefined {
    // Find the first text element that at least half after the time
    return this.words.find(
      el =>
        el.relative_end_ts > relativeTime &&
        relativeTime - el.relative_ts < el.relative_end_ts - relativeTime,
    );
  }

  /**
   * Filters transcript elements based on the time range, including relevant punctuation
   * @param originalTranscription The full array of transcript elements
   * @param mediaStartTime The start time of the segment to build
   * @param mediaEndTime The end time of the segment to build
   * @returns An array of filtered transcript elements present between the start and end times
   */
  static filterElements(
    transcriptionElements: readonly TranscriptElementLegacy[],
    fromTime: number,
    toTime: number | null,
  ): TranscriptElementLegacy[] {
    const textElements = transcriptionElements
      .filter((el): el is TranscriptTextToken => el.type === 'text')
      .filter(el => el.state !== 'removed' && el.state !== 'cut')
      .filter(
        (el): el is TranscriptTextToken & TimelinePositionPropsNotNull =>
          el.ts != null && el.end_ts != null,
      );

    // Find the first and last text elements within the time range
    // if more than half of the text element is within the time range
    const startIdx = textElements.findIndex(
      el =>
        el.end_ts > fromTime &&
        Math.abs(fromTime - el.ts) < Math.abs(el.end_ts - fromTime), // TODO: use precise arithmetic library
    );
    if (startIdx === -1) return []; // No text elements start after mediaStartTime

    // convert findLastIndex to use findIndex
    const endElement =
      toTime != null
        ? textElements
            .filter(
              el => el.ts < toTime && toTime - el.ts >= el.end_ts - toTime,
            )
            .at(-1)
        : textElements[textElements.length - 1];

    const lastIncludedIdx = endElement ? textElements.indexOf(endElement) : -1;
    if (lastIncludedIdx < startIdx) return []; // No elements within range

    // Map back to indices in the original transcription
    const startElementIdx = transcriptionElements.indexOf(
      textElements[startIdx],
    );
    const endElementIdx = transcriptionElements.indexOf(
      textElements[lastIncludedIdx],
    );

    // // Find the preceding and following words (text elements only)
    // this._precedingWord = this.findPrecedingTextElement(
    //   originalTranscription,
    //   mediaStartTimeAdjusted,
    // );
    // this._followingWord = this.findFollowingTextElement(
    //   originalTranscription,
    //   mediaEndTime, // mediaEndTimeAdjusted not needed
    // );

    // Include elements up to and including the punctuation after the last text, if present
    let includeUpTo = endElementIdx;
    while (includeUpTo + 1 < transcriptionElements.length) {
      if (
        includeUpTo + 1 < transcriptionElements.length &&
        transcriptionElements[includeUpTo + 1].type === 'punct'
      ) {
        includeUpTo = includeUpTo + 1;
      } else {
        break;
      }
    }

    return transcriptionElements.slice(startElementIdx, includeUpTo + 1);
  }

  /**
   * Gets the concatenated text of the segment, including punctuation
   * @returns The text string (e.g., "Hello world this ")
   */
  getText(): string {
    return this.wordsWithPunctuation.map(w => w.value).join('');
  }

  /**
   * Gets the words with timestamps, adjusted relative to the element's start time
   * Used by TranscriptRemoveCommand to determine which words to remove
   * @returns Array of ClipTranscriptToken with relative timestamps
   */
  static getElementsWithIdsAndTimelineRelativeTimestamps(
    transcriptTokens: TranscriptElementLegacy[],
    startTime: number,
    mediaStartTime: number,
    segmentDuration: number,
  ): ClipTranscriptToken[] {
    const result: ClipTranscriptToken[] = [];
    for (let i = 0; i < transcriptTokens.length; i++) {
      const el = transcriptTokens[i];

      // PLAT-10611 — preserve a pre-existing story-space id as source_uuid for
      // the translation join; a freshly minted id (below) is not an origin.
      if (el.id) {
        el.source_uuid ??= el.id;
      }

      // Assign IDs to each element if missing
      if (!el.id) {
        const generator = IdGenerator.get(
          IdGeneratorEntityType.TranscriptToken,
          'hex',
        );
        el.id = generator.getNextIdHex();
      }

      // Keep punctuation tokens as-is
      if (el.type !== 'text') {
        result.push({
          ...el,
          id: el.id,
        });
        continue;
      }

      // Skip text tokens missing required timestamps
      if (el.ts == null || el.end_ts == null) continue;

      const relativeStartTime = el.ts - startTime;
      const relativeEndTime = el.end_ts - startTime;

      // Convert cut points like `ts` only while `media_ts` is unset: after
      // that, `ts` is a timeline time and `cut_ts` no longer converts.
      const mediaCutTs =
        el.media_cut_ts ??
        (el.media_ts == null && el.cut_ts != null
          ? mediaStartTime + (el.cut_ts - startTime)
          : undefined);
      const mediaCutEndTs =
        el.media_cut_end_ts ??
        (el.media_end_ts == null && el.cut_end_ts != null
          ? mediaStartTime + (el.cut_end_ts - startTime)
          : undefined);

      result.push({
        ...el,
        id: el.id,
        relative_ts: Math.max(relativeStartTime, 0),
        relative_end_ts: Math.min(relativeEndTime, segmentDuration),
        media_ts: el.media_ts ?? mediaStartTime + relativeStartTime,
        media_end_ts: el.media_end_ts ?? mediaStartTime + relativeEndTime,
        ...(mediaCutTs != null && { media_cut_ts: mediaCutTs }),
        ...(mediaCutEndTs != null && { media_cut_end_ts: mediaCutEndTs }),
      });
    }
    return result;
  }

  /**
   * Gets all transcript elements (including punctuation) in the segment
   * @returns Array of all transcriptTokens
   */
  getWords(): ClipTranscriptTextToken[] {
    return this.words;
  }

  /**
   * Gets all transcript elements in this segment with both leading and trailing punctuation
   *
   * Returns all words in the segment plus any:
   * - Leading punctuation before the first word (up to the previous text element)
   * - Trailing punctuation after the last word (up to the next text element)
   *
   * @returns All segment words plus surrounding punctuation
   */
  getWordsWithPunctuation(): ClipTranscriptToken[] {
    return this.wordsWithPunctuation;
  }
}
